import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { translate } from '@ngneat/transloco';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, delay, expand, filter, finalize, reduce, take, takeUntil } from 'rxjs/operators';
import { GSTFilingState } from 'src/app/modules/taxilla-library/lib/store/states';

import { NotificationEvent } from '../../interface/notifications-event.interface';
import { TableColumn } from '../../interface/table-column.interface';
import { TableData } from '../../interface/table-data.interface';
import { AppConfigurator } from '../../models/app-configurator.class';
import { AssetData } from '../../models/assetdata.class';
import { AssetService } from '../../models/assetservice.class';
import { Entity } from '../../models/entity.class';
import { Field } from '../../models/field.class';
import { ReconciliationConfiguration } from '../../models/reconciliation-configuration.class';
import { Record } from '../../models/record/record.class';
import { WorkflowUserAction } from '../../models/workflowstagedata.class';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { UserPreferencesService } from '../../services/user-preferences/user-preferences.service';
import { UtilsService } from '../../services/utils/utils.service';
import { GetReconCardinalityCount } from '../../store/actions/aggregation.actions';
import { GetAssetWithLatestVersion, GetReconConfiguration } from '../../store/actions/apps.actions';
import {
    GetFilingInstanceEntityRecords,
    GetFilingInstancePrimaryEntityRecords,
    SetFilingInstanceEntityCriteria,
    SetFilingInstanceEntityIds,
    SetFilingInstanceEntityPagination,
    SetFilingInstanceEntityView,
    SetFilingSelectedInstanceDetails,
} from '../../store/actions/gst-filing.actions';
import { SetInstanceDataTableRecordSelect } from '../../store/actions/instances.actions';
import { ClearRecordsPagination, ClearSearchCriteria } from '../../store/actions/records.actions';
import { ExecutedWorkflowStage, ExecuteWorkflowStage } from '../../store/actions/workflow.actions';
import { getReconCardinalityCount$ } from '../../store/selectors/aggregation.selector';
import {
    getApp$,
    getAppConfiguratorsMap$,
    getAssetsWithLatestVersion$,
    getReconConfigurations$,
    getSubscribedAppsMap$,
} from '../../store/selectors/apps.selector';
import {
    getClearanceRunNumber$,
    getFilingDetachedSource1Records$,
    getFilingDetachedSource1RecordsCount$,
    getFilingDetachedSource1RecordsPagination$,
    getFilingDetachedSource2Records$,
    getFilingDetachedSource2RecordsCount$,
    getFilingDetachedSource2RecordsPagination$,
    getFilingEntityRecords$,
    getFilingEntityRecordsCount$,
    getFilingNonDetachedGroupRecords$,
    getFilingNonDetachedGroupRecordsCount$,
    getFilingNonDetachedGroupRecordsPagination$,
    getFilingParentEntityDetails$,
    getFilingPrimaryEntityRecords$,
    getFilingRecordsPagination$,
    getFilingSelectedAppMetaData$,
    getFilingSelectedEntity$,
    getFilingSelectedEntityDetails$,
    getFilingState$,
    getReconInstanceRecordsStatus$,
} from '../../store/selectors/gst-filing.selector';
import { getNotifications$ } from '../../store/selectors/notifications.selector';
import { getCurrentOrganizationId$ } from '../../store/selectors/session.selector';
import { getAvailableWorkflowActions$ } from '../../store/selectors/workflow.selector';
import { ColumnMatchingDialogComponent, ColumnMatchingDialogData } from '../column-matching-dialog/column-matching-dialog.component';
import {
    ToleranceDetailsDialogComponent,
    ToleranceDetailsDialogData,
} from '../tolerance-details-dialog/tolerance-details-dialog.component';

@Component({
    selector: 'app-gst-instance-reconcile-data-dialog',
    templateUrl: './instance-reconcile-data-dialog.component.html',
    styleUrls: ['./instance-reconcile-data-dialog.component.scss'],
    standalone: false,
})
export class InstanceReconcileDataDialogComponent implements OnInit {
    @Input() public demoMode = false;
    @Input() public isCustomScreen = false;
    @Input() public serviceId: string;
    @Input() public instanceId: string;
    @Input() public name: string;
    @Input() public reconRestApiName: string;

    private subscribedApps: { [property: string]: AssetService };
    private reconApp: AssetService;
    public reconConfiguration: ReconciliationConfiguration;
    public stateName: Observable<string>;
    public workflowActions: WorkflowUserAction[];
    public source1tableData: BehaviorSubject<TableData[]> = new BehaviorSubject([]);
    public source2tableData: BehaviorSubject<TableData[]> = new BehaviorSubject([]);
    public source1: string;
    public source2: string;
    public source1Details: ReconciliationConfiguration['sources'][0];
    public source2Details: ReconciliationConfiguration['sources'][0];
    public source1_allColumns: BehaviorSubject<TableColumn[]> = new BehaviorSubject([]);
    public source2_allColumns: BehaviorSubject<TableColumn[]> = new BehaviorSubject([]);
    public source1_selectedColumns: string[] = [];
    public source2_selectedColumns: string[] = [];
    public source1SearchableField: string[] = [];
    public source2SearchableField: string[] = [];
    private isApplyingMatchedColumns = false;
    public nonDetachedEntity: Entity;
    private assetMetaData: AssetData;
    public entities: Entity[] = [];
    private unSubscribe = new Subject<void>();
    public isExporting: { source1: boolean; source2: boolean } = { source1: false, source2: false };
    public isConfigurationLoaded = false;
    private currentView: { recordId: string; entityId: string; level: number };
    public primaryEntity: Entity;
    public selectedToleranceFieldId: string;
    private primaryEntityRecord: Record;
    public source1Pagination = {
        index: 0,
        size: 20,
        count: 0,
    };
    public source2Pagination = {
        index: 0,
        size: 20,
        count: 0,
    };
    public dataLength: number;
    public loadingStatus: Observable<boolean>;
    public clearanceRunNumber: number;
    public selectedRecordsCount = 0;
    public moveToGroup: string;
    private entityRecords: {
        source1: Record[];
        source2: Record[];
    } = {
        source1: [],
        source2: [],
    };
    public dialogRef: any;
    public canShowMovableEntities: boolean;
    public filteredNonSelectedEntities: Entity[];
    private searchedColumnsData: { name: ''; value: '' }[] = [];
    private organizationId: string;
    private pageIndexVsRecords: {
        source1: {
            [index: number]: {
                tableData: TableData[];
                records: Record[];
            };
        };
        source2: {
            [index: number]: {
                tableData: TableData[];
                records: Record[];
            };
        };
    } = {
        source1: {},
        source2: {},
    };
    public selectedGroup: ReconciliationConfiguration['groups'][0];
    private nonDetachedGroupNameFieldId: string;
    private source1DetachedGroupNameFieldId: string;
    private source2DetachedGroupNameFieldId: string;
    private source1UniqueId: string;
    private source2UniqueId: string;
    private associationId: string;
    public selectedWorkflowAction: WorkflowUserAction;
    private instanceCutoffTimestamp: number;
    public workflowLoadingState: boolean;
    public isDetached: boolean;
    private nonDetachedEntityId: string;
    private detachedSource1EntityId: string;
    private detachedSource2EntityId: string;
    public detachedSource1Entity: Entity;
    public detachedSource2Entity: Entity;
    private detachedSource1UniqueFieldId: string;
    private detachedSource2UniqueFieldId: string;
    public nonReconEntities: Entity[] = [];
    public isReconEntity = true;
    public selectedEntity: Entity;
    public selectedDetails: GSTFilingState['selected']['viewLevel']['level'];
    public app: AssetService;
    private selectedAppConfigurator: AppConfigurator;
    public aggregationcardinalityCountForSource1: number | string;
    public aggregationcardinalityCountForSource2: number | string;
    public documentIdentifierField: {
        uid: string;
        entityUid: string;
        searchableIndex: string;
        dataType: string;
        groupFieldSearchableIndex: string;
    };
    public reconCriteriaSource1: any = {};
    public reconCriteriaSource2: any = {};
    public clearAggregationData: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public actualAssetId: string;
    public table1_totalCount = 0;
    public table2_totalCount = 0;
    public variance_totalCount = 0;
    public selectedTable1Id: string;
    public selectedTable2Id: string;
    public table1NumericFields: any[] = [];
    public table2NumericFields: any[] = [];
    public previousSelectedEntityUid: string;

    constructor(
        private dialog: MatDialog,
        private store$: Store,
        private actions$: Actions,
        private _utils: UtilsService,
        public _commonUtils: CommonUtilsService,
        private userPreferences: UserPreferencesService,
        private _api: ApiService
    ) {}

    public closeModal = () => {
        this.demoMode ? this.dialogRef.close() : this.dialog.closeAll();
    };

    private addCheckboxColumn = () => {
        return {
            id: 'SELECTRECORD',
            hide: !this.isReconEntity,
            hideInAllColumnsList: true,
            icon: undefined,
            name: 'CheckBox',
            type: 'SELECTRECORD',
            options: undefined,
            showCheckboxInHeader: this.isReconEntity,
        };
    };

    private toleranceWarningOpen = false;

    private validateTolerances = (onProceed?: () => void): boolean => {
        if (this.toleranceWarningOpen) {
            return false;
        }
        const toleranceFields =
            this.primaryEntity?.fields?.filter(
                field =>
                    ['DOUBLE', 'DECIMAL', 'FLOAT', 'NUMBER', 'INT', 'LONG', 'INTEGER'].includes(field?.datatype) &&
                    (field?.name?.toLowerCase().includes('tolerance') ||
                        field?.displayName?.toLowerCase().includes('tolerance') ||
                        field?.uid?.toLowerCase().includes('tolerance'))
            ) || [];

        if (toleranceFields.length === 0) {
            return true;
        }

        const selectedRecords = this.getSelectedRecords();
        const total1 = selectedRecords.source1?.reduce((total, record) => {
            return total + CommonUtilsService.toNumber(record.fields.find(field => field.id === this.selectedTable1Id)?.value);
        }, 0);

        const total2 = selectedRecords.source2?.reduce((total, record) => {
            return total + CommonUtilsService.toNumber(record.fields.find(field => field.id === this.selectedTable2Id)?.value);
        }, 0);

        const variance = total1 - total2;
        const failingFields: ToleranceDetailsDialogData['failingFields'] = [];

        toleranceFields.forEach(field => {
            const recordField = this.primaryEntityRecord?.fields?.find(recordField => recordField.id === field.uid);
            const toleranceValue = CommonUtilsService.toNumber(recordField?.value);
            if (!isNaN(toleranceValue) && Math.abs(variance) > Math.abs(toleranceValue)) {
                failingFields.push({
                    name: field.displayName || field.name || field.uid,
                    tolerance: toleranceValue,
                    exceededBy: Math.abs(variance) - Math.abs(toleranceValue),
                });
            }
        });

        if (failingFields.length > 0) {
            this.toleranceWarningOpen = true;
            const dialogRef = this.dialog.open<ToleranceDetailsDialogComponent, ToleranceDetailsDialogData, boolean>(
                ToleranceDetailsDialogComponent,
                {
                    width: '780px',
                    maxWidth: '95vw',
                    maxHeight: '90vh',
                    panelClass: 'toleranceWarningDialog',
                    role: 'alertdialog',
                    autoFocus: '.warning-cancel',
                    data: {
                        sources: [
                            {
                                name: this.source1Details?.name || this.source1 || translate('Source 1'),
                                column:
                                    this.table1NumericFields.find(column => column.id === this.selectedTable1Id)?.name ||
                                    this.selectedTable1Id ||
                                    translate('Not selected'),
                                recordCount: selectedRecords.source1?.length || 0,
                                total: total1,
                            },
                            {
                                name: this.source2Details?.name || this.source2 || translate('Source 2'),
                                column:
                                    this.table2NumericFields.find(column => column.id === this.selectedTable2Id)?.name ||
                                    this.selectedTable2Id ||
                                    translate('Not selected'),
                                recordCount: selectedRecords.source2?.length || 0,
                                total: total2,
                            },
                        ],
                        variance,
                        failingFields,
                    } as ToleranceDetailsDialogData,
                }
            );
            dialogRef
                .afterClosed()
                .pipe(
                    take(1),
                    takeUntil(this.unSubscribe),
                    finalize(() => (this.toleranceWarningOpen = false))
                )
                .subscribe(confirmed => {
                    if (confirmed === true) {
                        onProceed?.();
                    }
                });
            return false;
        }
        return true;
    };

    private loadTableData = (records: Record[], sourceType?: 'source1' | 'source2' | 'grouped') => {
        switch (sourceType) {
            case 'source2':
                this.entityRecords.source2 = records;
                break;
            default:
                this.entityRecords.source1 = records;
                break;
        }

        const { source1Rows, source2Rows } = this.convertToTableData(records, sourceType);

        (!sourceType || sourceType === 'source1') && this.source1tableData.next(source1Rows);
        (!sourceType || sourceType === 'source2') && this.source2tableData.next(source2Rows);
        this.isConfigurationLoaded = true;
        this.prepareTotalCount();
    };

    private convertToTableData = (records: Record[], sourceType?: 'source1' | 'source2' | 'grouped') => {
        const source1Rows: TableData[] = [];
        const source2Rows: TableData[] = [];
        const source1AssociationIds = [];
        const source2AssociationIds = [];
        const mainAssociationIds = [];
        let isDifferentGroup: boolean;
        records?.forEach(record => {
            const errors = record?.errors?.filter(error => !error?.match);
            const row: TableData = {
                recordId: record?.id,
                disabled: false,
                errors: errors as any,
                errorsCount: errors?.length,
                score: record?.['score'],
                hideSource1Checkbox: false,
                hideSource2Checkbox: false,
            };
            let isSource1Record: boolean, isSource2Record: boolean;
            if (!this.isReconEntity) {
                isSource1Record = true;
                this.selectedEntity.fields.forEach(field => {
                    this.assignValue(
                        row,
                        field.uid,
                        field.datatype,
                        (record as Record).fields.find(fieldData => fieldData.id === field.uid)?.value,
                        field.valueDisplayFormat
                    );
                });
                row.hideSource1Checkbox = true;
                row.hideSource2Checkbox = true;
            } else if (this.isDetached) {
                isSource1Record = sourceType === 'source1';
                isSource2Record = sourceType === 'source2';
                const entity = sourceType === 'source1' ? this.detachedSource1Entity : this.detachedSource2Entity;
                entity.fields.forEach(field => {
                    this.assignValue(
                        row,
                        field.uid,
                        field.datatype,
                        (record as Record).fields.find(fieldData => fieldData.id === field.uid)?.value,
                        field.valueDisplayFormat
                    );
                });
                row.hideSource2Checkbox = isSource1Record;
                row.hideSource1Checkbox = isSource2Record;
            } else if (!this.isDetached) {
                const source1Value = record.fields.find(field => field.id === this.source1UniqueId)?.value;
                const source2Value = record.fields.find(field => field.id === this.source2UniqueId)?.value;
                const mainValue = record.fields.find(field => field.id === this.associationId)?.value as string;
                isDifferentGroup = !mainAssociationIds.includes(mainValue);
                isDifferentGroup && mainAssociationIds.push(mainValue);
                const isSource1Duplicated = source1AssociationIds.includes(source1Value);
                const isSource2Duplicated = source2AssociationIds.includes(source2Value);
                !isSource1Duplicated && source1Value && source1AssociationIds.push(source1Value);
                !isSource2Duplicated && source2Value && source2AssociationIds.push(source2Value);
                isSource1Record = !isSource1Duplicated && source1Value !== undefined;
                isSource2Record = !isSource2Duplicated && source2Value !== undefined;
                row.bgColor = ['#FFF', '#f3f5fe'][mainAssociationIds.indexOf(mainValue) % 2];
                row.mainAssociationId = mainValue;
                this.nonDetachedEntity.fields
                    .filter(field =>
                        isSource1Record && !isSource2Record ? !field.fieldSection || field.fieldSection === this.source1 : true
                    )
                    .filter(field =>
                        isSource2Record && !isSource1Record ? !field.fieldSection || field.fieldSection === this.source2 : true
                    )
                    .forEach(field => {
                        this.assignValue(
                            row,
                            field.uid,
                            field.datatype,
                            (record as Record).fields.find(fieldData => fieldData.id === field.uid)?.value,
                            field.valueDisplayFormat
                        );
                    });
                row.hideSource1Checkbox = !isSource1Record;
                row.hideSource2Checkbox = !isSource2Record;
            } else {
                (isSource1Record = true), (isSource2Record = true);
                this.nonDetachedEntity.fields?.forEach(field => {
                    this.assignValue(
                        row,
                        field.uid,
                        field.datatype,
                        (record as Record).fields.find(fieldData => fieldData.id === field.uid)?.value,
                        field.valueDisplayFormat
                    );
                });
            }
            row.selected = record.selected ?? false;
            !this.isDetached && isDifferentGroup && this.pushDifference(source1Rows, source2Rows);
            isSource1Record && source1Rows.push(row);
            isSource2Record && source2Rows.push(row);
        });
        !this.isDetached && this.pushDifference(source1Rows, source2Rows);
        return { source1Rows, source2Rows };
    };
    public onSelectedColumnChange(source: 'source1' | 'source2', columnId: string | null) {
        if (source === 'source1') {
            this.selectedTable1Id = columnId;
        } else {
            this.selectedTable2Id = columnId;
        }

        // Save selected column for total to user preferences
        const appRestApiName = this.reconRestApiName || 'default';
        const entity1 = this.isDetached
            ? this.detachedSource1Entity?.uid
            : this.isReconEntity
            ? this.nonDetachedEntity?.uid
            : this.selectedEntity?.uid;
        const entity2 = this.isDetached
            ? this.detachedSource2Entity?.uid
            : this.isReconEntity
            ? this.nonDetachedEntity?.uid
            : this.selectedEntity?.uid;

        if (entity1 && entity2) {
            const totalColumnKey1 = `apps.${appRestApiName}.reconciliation.${entity1}.source1.aggregation`;
            const totalColumnKey2 = `apps.${appRestApiName}.reconciliation.${entity2}.source2.aggregation`;

            if (source === 'source1') {
                this.userPreferences.setPreference(totalColumnKey1, columnId);
            } else {
                this.userPreferences.setPreference(totalColumnKey2, columnId);
            }
        }

        this.prepareTotalCount();
    }

    public openColumnMatchingDialog(source: 'source1' | 'source2'): void {
        const selectedColumns = (source === 'source1' ? this.source1_allColumns : this.source2_allColumns).value.filter(col =>
            (source === 'source1' ? this.source1_selectedColumns : this.source2_selectedColumns).includes(col.id)
        );

        if (!selectedColumns || selectedColumns.length === 0) {
            return;
        }

        const oppositeColumns = (source === 'source1' ? this.source2_allColumns : this.source1_allColumns).value.filter(
            col => col.id !== 'SELECTRECORD' && !col.hideInAllColumnsList
        );

        const dialogRef = this.dialog.open(ColumnMatchingDialogComponent, {
            width: '600px',
            maxWidth: '90vw',
            data: {
                selectedColumns,
                oppositeColumns,
                sourceSide: source,
            } as ColumnMatchingDialogData,
        });

        dialogRef.afterClosed().subscribe(matchedColumns => {
            if (matchedColumns && matchedColumns.length > 0) {
                this.applyMatchedColumns(source, matchedColumns);
            }
        });
    }

    private applyMatchedColumns(source: 'source1' | 'source2', matchedColumns: TableColumn[]): void {
        const targetSource = source === 'source1' ? 'source2' : 'source1';
        const targetColumns = targetSource === 'source1' ? this.source1_allColumns : this.source2_allColumns;

        // Replace selected columns with matched columns only
        const newSelectedColumns = matchedColumns.map(col => col.id);

        // Update selected columns array
        if (targetSource === 'source1') {
            this.source1_selectedColumns = [...newSelectedColumns];
        } else {
            this.source2_selectedColumns = [...newSelectedColumns];
        }

        // Ensure matched columns are visible and hide others
        targetColumns.value.forEach(column => {
            column.hide = !newSelectedColumns.includes(column.id) && column.id !== 'SELECTRECORD';
        });

        // Set flag to prevent infinite loop
        this.isApplyingMatchedColumns = true;

        // Trigger column change to update the table
        this.onColumnChangeFromTable({
            columns: newSelectedColumns,
            source: targetSource,
        });

        // Reset flag after a short delay to ensure column change is processed
        setTimeout(() => {
            this.isApplyingMatchedColumns = false;
        }, 100);
    }

    private sanitizeCsvValue = (value: any): string => {
        let stringValue = value === undefined || value === null ? '' : String(value);
        // Neutralize CSV formula injection when the file is opened in spreadsheet software
        if (/^[=+\-@]/.test(stringValue)) {
            stringValue = `'${stringValue}`;
        }
        return /["\n,]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
    };

    public exportTableData = (source: 'source1' | 'source2', mode: 'selected' | 'all') => {
        if (mode === 'selected') {
            this.exportRows(source, this.buildSelectedExportRows(source), mode);
            return;
        }
        this.exportAllRecords(source);
    };

    /**
     * Selected rows may span pages the user is no longer viewing. pageIndexVsRecords already
     * accumulates the per-page selected TableData (see prepareIndexVsSelectedRecords / getSelectedTableData),
     * so it's the single source of truth for "everything the user checked" across pagination.
     */
    private buildSelectedExportRows = (source: 'source1' | 'source2'): TableData[] => {
        this.prepareIndexVsSelectedRecords();
        let rows: TableData[] = [];
        Object.values(this.pageIndexVsRecords[source]).forEach(pageEntry => {
            rows = [...rows, ...pageEntry.tableData];
        });
        return rows.filter(row => row.recordId !== undefined);
    };

    private exportAllRecords = (source: 'source1' | 'source2') => {
        // Prevent duplicate exports while one is already in progress
        if (this.isExporting[source]) {
            return;
        }

        const totalCount = this.isDetached
            ? source === 'source1'
                ? this.source1Pagination.count
                : this.source2Pagination.count
            : this.dataLength;

        if (!totalCount || totalCount === 0) {
            this._utils.alertError(translate('No records available to export.'));
            return;
        }

        const obj = {};
        for (let i = 0; i < this.searchedColumnsData.length; i++) {
            this.searchedColumnsData[i].value && (obj[this.searchedColumnsData[i].name] = this.searchedColumnsData[i].value);
        }

        const criteria: any =
            this.isReconEntity && this.isDetached
                ? this.prepareCriteria(obj, { name: source === 'source1' ? this.source1 : this.source2, source }, false)
                : this.prepareCriteria(obj);

        const entityId = this.isDetached
            ? source === 'source1'
                ? this.detachedSource1EntityId
                : this.detachedSource2EntityId
            : this.nonDetachedEntityId;

        // Page size for each background fetch: large enough to minimise round trips
        // but within typical backend limits (e.g. 200).
        const PAGE_SIZE = 200;

        this.isExporting[source] = true;

        // Use RxJS expand to paginate through all records without a mutable
        // accumulator. Each emission is { records, searchAfter, done }.
        // expand recurses until done === true, then EMPTY terminates the chain.
        this._api.records
            .searchEntityRecordsObservable({
                assetName: this.app?.serviceName,
                query: { ...criteria },
                searchAfter: undefined,
                searchEntityId: entityId,
                restApiServiceName: this.app?.restApiName,
                size: PAGE_SIZE,
                noAlerts: false,
            })
            .pipe(
                // expand re-invokes itself with the next page cursor until the
                // API returns fewer than PAGE_SIZE records or no searchAfter cursor.
                expand((response: any) => {
                    const fetched: any[] = response?.records || [];
                    const nextCursor = response?.searchAfter ?? null;
                    const hasMore = nextCursor && fetched.length === PAGE_SIZE;

                    if (!hasMore) {
                        return EMPTY;
                    }

                    return this._api.records.searchEntityRecordsObservable({
                        assetName: this.app?.serviceName,
                        query: { ...criteria },
                        searchAfter: nextCursor,
                        searchEntityId: entityId,
                        restApiServiceName: this.app?.restApiName,
                        size: PAGE_SIZE,
                        noAlerts: false,
                    });
                }),
                // Collect every page's records into one flat array
                reduce((all: any[], response: any) => [...all, ...(response?.records || [])], []),
                takeUntil(this.unSubscribe),
                catchError(() => {
                    this._utils.alertError(translate('Failed to export records. Please try again.'));
                    this.isExporting[source] = false;
                    return EMPTY;
                })
            )
            .subscribe((allRecords: any[]) => {
                this.isExporting[source] = false;
                this.exportFetchedRecords(source, allRecords);
            });
    };

    private exportFetchedRecords = (source: 'source1' | 'source2', records: any[]) => {
        const entity = this.isReconEntity
            ? this.isDetached
                ? source === 'source1'
                    ? this.detachedSource1Entity
                    : this.detachedSource2Entity
                : this.nonDetachedEntity
            : this.selectedEntity;

        const rowsToExport: TableData[] = records.map(responseRecord => {
            const record = new Record(responseRecord, entity);
            const errors = record?.errors?.filter(error => !error?.match);

            const row: TableData = {
                recordId: record?.id,
                disabled: false,
                errors: errors as any,
                errorsCount: errors?.length,
                score: record?.['score'],
                hideSource1Checkbox: false,
                hideSource2Checkbox: false,
            };

            entity?.fields?.forEach(field => {
                this.assignValue(
                    row,
                    field.uid,
                    field.datatype,
                    record.fields.find(f => f.id === field.uid)?.value,
                    field.valueDisplayFormat
                );
            });

            return row;
        });

        this.exportRows(source, rowsToExport, 'all');
    };

    private exportRows = (source: 'source1' | 'source2', rows: TableData[], mode: 'selected' | 'all') => {
        const allColumns = (source === 'source1' ? this.source1_allColumns : this.source2_allColumns).value;
        const selectedColumnIds = source === 'source1' ? this.source1_selectedColumns : this.source2_selectedColumns;
        const columns = selectedColumnIds.map(id => allColumns.find(col => col.id === id)).filter(col => !!col);

        const exportableRows = rows.filter(row => row.recordId !== undefined);

        if (columns.length === 0 || exportableRows.length === 0) {
            this._utils.alertError(
                mode === 'selected'
                    ? translate('Please select at least one record to export.')
                    : translate('No records available to export.')
            );
            return;
        }

        const csvLines = [
            columns.map(col => this.sanitizeCsvValue(col.displayName || col.name)).join(','),
            ...exportableRows.map(row => columns.map(col => this.sanitizeCsvValue(row[col.id])).join(',')),
        ];
        const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const sourceName =
            (source === 'source1' ? this.source1Details?.name || this.source1 : this.source2Details?.name || this.source2) || source;
        const link = document.createElement('a');
        link.href = url;
        link.download = `${sourceName}_${mode}_records.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    public prepareTotalCount = () => {
        const selectedRecords = this.getSelectedRecords();
        const total1 = selectedRecords.source1?.reduce((total, record) => {
            return total + CommonUtilsService.toNumber(record.fields.find(field => field.id === this.selectedTable1Id)?.value);
        }, 0);

        this.table1_totalCount = CommonUtilsService.formatNumber(total1, '#,###,###.00');

        const total2 = selectedRecords.source2?.reduce((total, record) => {
            return total + CommonUtilsService.toNumber(record.fields.find(field => field.id === this.selectedTable2Id)?.value);
        }, 0);

        this.table2_totalCount = CommonUtilsService.formatNumber(total2, '#,###,###.00');

        const variance = total1 - total2;
        this.variance_totalCount = CommonUtilsService.formatNumber(variance, '#,###,###.00');
    };

    private pushDifference = (rows1: TableData[], rows2: TableData[]) => {
        const count = rows1.length - rows2.length;
        if (count > 0) {
            CommonUtilsService.getRange(0, count - 1).forEach(() =>
                rows2.push({
                    hideSource2Checkbox: true,
                    recordId: undefined,
                    bgColor: rows1[rows1.length - 1].bgColor,
                })
            );
        }
        if (count < 0) {
            CommonUtilsService.getRange(0, Math.abs(count) - 1).forEach(() =>
                rows1.push({
                    hideSource1Checkbox: true,
                    recordId: undefined,
                    bgColor: rows2[rows2.length - 1].bgColor,
                })
            );
        }
    };

    private assignValue = (row: TableData, id: string, dataType: string, value: any, valueDisplayFormat: string) => {
        switch (dataType) {
            case 'INT':
            case 'LONG':
            case 'DOUBLE':
            case 'DECIMAL':
            case 'FLOAT':
                row[id] = CommonUtilsService.formatNumber(value, valueDisplayFormat);
                break;
            default:
                row[id] = value;
                break;
        }
    };

    private onGroupSelectionChange = () => {
        this.clearRecordsPagination();
        this.getSelectedEntityRecords();
    };

    private prepareTableColumns = () => {
        this.resetSelection();
        const source1allColumns = [];
        const source2allColumns = [];
        this.source1_selectedColumns = [];
        this.source2_selectedColumns = [];
        source1allColumns.push(this.addCheckboxColumn());
        source2allColumns.push(this.addCheckboxColumn());

        // First check if user preferences exist
        const appRestApiName = this.reconRestApiName || 'default';
        const entity1 = this.isDetached
            ? this.detachedSource1Entity?.uid
            : this.isReconEntity
            ? this.nonDetachedEntity?.uid
            : this.selectedEntity?.uid;
        const entity2 = this.isDetached
            ? this.detachedSource2Entity?.uid
            : this.isReconEntity
            ? this.nonDetachedEntity?.uid
            : this.selectedEntity?.uid;

        if (!entity1 || !entity2) {
            // No entity available, skip loading preferences and use default columns
            this.buildDefaultColumns(source1allColumns, source2allColumns);
            this.source1_allColumns.next(source1allColumns);
            this.source2_allColumns.next(source2allColumns);
            this.source1_allColumns.pipe(takeUntil(this.unSubscribe)).subscribe(columns => {
                this.table1NumericFields = [];
                const numericColumns =
                    columns?.filter(column => ['DOUBLE', 'DECIMAL', 'FLOAT', 'INTEGER'].includes(column?.inputType)) || [];
                this.table1NumericFields.push(...numericColumns);
            });
            this.source2_allColumns.pipe(takeUntil(this.unSubscribe)).subscribe(columns => {
                this.table2NumericFields = [];
                const numericColumns =
                    columns?.filter(column => ['DOUBLE', 'DECIMAL', 'FLOAT', 'INTEGER'].includes(column?.inputType)) || [];
                this.table2NumericFields.push(...numericColumns);
            });
            return;
        }

        const key1 = `apps.${appRestApiName}.reconciliation.${entity1}.source1.table.fields`;
        const key2 = `apps.${appRestApiName}.reconciliation.${entity2}.source2.table.fields`;
        const totalColumnKey1 = `apps.${appRestApiName}.reconciliation.${entity1}.source1.aggregation`;
        const totalColumnKey2 = `apps.${appRestApiName}.reconciliation.${entity2}.source2.aggregation`;

        const config1 = this.userPreferences.getPreference<{ [key: string]: any }>(key1);
        const config2 = this.userPreferences.getPreference<{ [key: string]: any }>(key2);

        // Load selected column for total from preferences
        this.selectedTable1Id = this.userPreferences.getPreference<string>(totalColumnKey1);
        this.selectedTable2Id = this.userPreferences.getPreference<string>(totalColumnKey2);

        // If preferences exist, build columns from preferences
        this.buildColumnsFromPreferences(source1allColumns, source2allColumns, config1, config2);

        this.source1_allColumns.next(source1allColumns);
        this.source2_allColumns.next(source2allColumns);
        this.source1_allColumns.pipe(takeUntil(this.unSubscribe)).subscribe(columns => {
            this.table1NumericFields = [];
            const numericColumns = columns?.filter(column => ['DOUBLE', 'DECIMAL', 'FLOAT', 'INTEGER'].includes(column?.inputType)) || [];

            this.table1NumericFields.push(...numericColumns);
        });
        this.source2_allColumns.pipe(takeUntil(this.unSubscribe)).subscribe(columns => {
            this.table2NumericFields = [];
            const numericColumns = columns?.filter(column => ['DOUBLE', 'DECIMAL', 'FLOAT', 'INTEGER'].includes(column?.inputType)) || [];

            this.table2NumericFields.push(...numericColumns);
        });

        // Set default to first numeric field (Document Value) if no preference exists
        if (!this.selectedTable1Id && this.table1NumericFields.length > 0) {
            this.selectedTable1Id = this.table1NumericFields[0].id;
        }
        if (!this.selectedTable2Id && this.table2NumericFields.length > 0) {
            this.selectedTable2Id = this.table2NumericFields[0].id;
        }
    };

    private prepareColumnDetails = (field: Field): TableColumn => {
        return {
            hide: false,
            icon: undefined,
            id: field.uid,
            name: field.displayName || field.name,
            type: 'text' as const,
            searchable: field.searchable,
            inputType: field.datatype,
        };
    };

    /**
     * Save column preferences to localStorage
     */
    private saveColumnPreferences = (source: 'source1' | 'source2', columns: TableColumn[]) => {
        const appRestApiName = this.reconRestApiName || 'default';
        const entity =
            source === 'source1'
                ? this.isDetached
                    ? this.detachedSource1Entity?.uid
                    : this.isReconEntity
                    ? this.nonDetachedEntity?.uid
                    : this.selectedEntity?.uid
                : this.isDetached
                ? this.detachedSource2Entity?.uid
                : this.isReconEntity
                ? this.nonDetachedEntity?.uid
                : this.selectedEntity?.uid;

        if (!entity) {
            // No entity available, skip saving preferences
            return;
        }

        const key = `apps.${appRestApiName}.reconciliation.${entity}.${source}.table.fields`;

        const fieldsConfig: { [key: string]: { selected: boolean; order: number } } = {};
        const selectedColumns = source === 'source1' ? this.source1_selectedColumns : this.source2_selectedColumns;
        let orderIndex = 0;
        columns.forEach(column => {
            // Skip internal table columns and only save selected fields
            if (column.id !== 'SELECTRECORD' && selectedColumns.includes(column.id)) {
                fieldsConfig[column.id] = {
                    selected: true,
                    order: orderIndex++,
                };
            }
        });

        this.userPreferences.setPreference(key, fieldsConfig);
    };

    /**
     * Apply saved preferences to columns array
     */
    private applyColumnPreferences = (columns: TableColumn[], preferences: { [key: string]: { selected: boolean; order: number } }) => {
        columns.sort((a, b) => {
            const orderA = preferences?.[a.id]?.order ?? 999;
            const orderB = preferences?.[b.id]?.order ?? 999;
            return orderA - orderB;
        });
    };

    private getVisibleFields = (fields: Field[]): Field[] => (fields || []).filter(field => field?.show !== false);

    /**
     * Build columns from saved user preferences
     */
    private buildColumnsFromPreferences = (
        source1allColumns: TableColumn[],
        source2allColumns: TableColumn[],
        config1: { [key: string]: any },
        config2: { [key: string]: any }
    ) => {
        // Build source1 columns
        if (!this.isReconEntity) {
            this.getVisibleFields(this.selectedEntity.fields).forEach(field => {
                source1allColumns.push(this.prepareColumnDetails(field));
                field.searchableIndex && this.source1SearchableField.push(field.uid);
            });
        } else if (!this.isDetached) {
            this.getVisibleFields(this.nonDetachedEntity.fields).forEach(field => {
                if (this.source1 && field?.fieldSection === this.source1) {
                    source1allColumns.push(this.prepareColumnDetails(field));
                    field.searchableIndex && this.source1SearchableField.push(field.uid);
                }
            });
        } else {
            this.getVisibleFields(this.detachedSource1Entity.fields).forEach(field => {
                source1allColumns.push(this.prepareColumnDetails(field));
                field.searchableIndex && this.source1SearchableField.push(field.uid);
            });
        }
        this.applyColumnPreferences(source1allColumns, config1);
        this.source1_selectedColumns = source1allColumns
            .filter(col => col.id !== 'SELECTRECORD')
            .sort((a, b) => (config1?.[a.id]?.order ?? 999) - (config1?.[b.id]?.order ?? 999))
            .filter((col, index) => (config1 ? config1?.[col.id] : index < 4))
            .map(col => col.id);

        // Build source2 columns
        if (!this.isReconEntity) {
            this.getVisibleFields(this.selectedEntity.fields).forEach(field => {
                source2allColumns.push(this.prepareColumnDetails(field));
                field.searchableIndex && this.source2SearchableField.push(field.uid);
            });
        } else if (!this.isDetached) {
            this.getVisibleFields(this.nonDetachedEntity.fields).forEach(field => {
                if (this.source2 && field?.fieldSection === this.source2) {
                    source2allColumns.push(this.prepareColumnDetails(field));
                    field.searchableIndex && this.source2SearchableField.push(field.uid);
                }
            });
        } else {
            this.getVisibleFields(this.detachedSource2Entity.fields).forEach(field => {
                source2allColumns.push(this.prepareColumnDetails(field));
                field.searchableIndex && this.source2SearchableField.push(field.uid);
            });
        }
        this.applyColumnPreferences(source2allColumns, config2);
        this.source2_selectedColumns = source2allColumns
            .filter(col => col.id !== 'SELECTRECORD')
            .sort((a, b) => (config2?.[a.id]?.order ?? 999) - (config2?.[b.id]?.order ?? 999))
            .filter((col, index) => (config2 ? config2?.[col.id] : index < 4))
            .map(col => col.id);
    };

    /**
     * Build default columns from entity fields (when no preferences exist)
     */
    private buildDefaultColumns = (source1allColumns: TableColumn[], source2allColumns: TableColumn[]) => {
        this.buildSource1DefaultColumns(source1allColumns);
        this.buildSource2DefaultColumns(source2allColumns);
    };

    /**
     * Build default source1 columns from entity fields
     */
    private buildSource1DefaultColumns = (source1allColumns: TableColumn[]) => {
        if (!this.isReconEntity) {
            this.getVisibleFields(this.selectedEntity.fields).forEach(field => {
                source1allColumns.push(this.prepareColumnDetails(field));
                field.searchableIndex && this.source1SearchableField.push(field.uid);
            });
            this.getVisibleFields(this.selectedEntity.fields)
                .sort(this._utils.sortFields)
                .forEach(field => {
                    this.source1_selectedColumns.length < 4 && this.source1_selectedColumns.push(field.uid);
                });
        } else if (!this.isDetached) {
            this.getVisibleFields(this.nonDetachedEntity.fields).forEach(field => {
                if (this.source1 && field?.fieldSection === this.source1) {
                    source1allColumns.push(this.prepareColumnDetails(field));
                    this.source1_selectedColumns.length < 4 && this.source1_selectedColumns.push(field.uid);
                    field.searchableIndex && this.source1SearchableField.push(field.uid);
                }
            });
        } else {
            this.getVisibleFields(this.detachedSource1Entity.fields).forEach(field => {
                source1allColumns.push(this.prepareColumnDetails(field));
                this.source1_selectedColumns.length < 4 && this.source1_selectedColumns.push(field.uid);
                field.searchableIndex && this.source1SearchableField.push(field.uid);
            });
        }
    };

    /**
     * Build default source2 columns from entity fields
     */
    private buildSource2DefaultColumns = (source2allColumns: TableColumn[]) => {
        if (!this.isReconEntity) {
            this.getVisibleFields(this.selectedEntity.fields).forEach(field => {
                source2allColumns.push(this.prepareColumnDetails(field));
                field.searchableIndex && this.source2SearchableField.push(field.uid);
            });
            this.getVisibleFields(this.selectedEntity.fields)
                .sort(this._utils.sortFields)
                .forEach(field => {
                    this.source2_selectedColumns.length < 4 && this.source2_selectedColumns.push(field.uid);
                });
        } else if (!this.isDetached) {
            this.getVisibleFields(this.nonDetachedEntity.fields).forEach(field => {
                if (this.source2 && field?.fieldSection === this.source2) {
                    source2allColumns.push(this.prepareColumnDetails(field));
                    this.source2_selectedColumns.length < 4 && this.source2_selectedColumns.push(field.uid);
                    field.searchableIndex && this.source2SearchableField.push(field.uid);
                }
            });
        } else {
            this.getVisibleFields(this.detachedSource2Entity.fields).forEach(field => {
                source2allColumns.push(this.prepareColumnDetails(field));
                this.source2_selectedColumns.length < 4 && this.source2_selectedColumns.push(field.uid);
                field.searchableIndex && this.source2SearchableField.push(field.uid);
            });
        }
    };

    /**
     * Handle column change event from table component
     */
    public onColumnChangeFromTable = (event: { columns: string[]; source: string }) => {
        const source = event.source === 'source1' ? 'source1' : 'source2';
        const columns = source === 'source1' ? this.source1_allColumns.value : this.source2_allColumns.value;

        // Internal columns to preserve
        const internalColumns = ['SELECTRECORD', 'table-actions', 'log', 'table-checkbox'];
        const preservedInternalColumns = columns.filter(col => internalColumns.includes(col.id));

        // Update column visibility based on selected columns
        columns.forEach(column => {
            column.hide = !event.columns.includes(column.id) && !internalColumns.includes(column.id);
        });

        // Reorder columns: selected columns first (in selected order), then hidden columns, then internal columns
        const visibleColumns = event.columns.map(colId => columns.find(col => col.id === colId)).filter(col => col !== undefined);
        const hiddenColumns = columns.filter(col => col.hide && !internalColumns.includes(col.id));
        const reorderedColumns = [...visibleColumns, ...hiddenColumns, ...preservedInternalColumns];

        if (source === 'source1') {
            this.source1_allColumns.next(reorderedColumns);
            this.source1_selectedColumns = [...event.columns];
        } else {
            this.source2_allColumns.next(reorderedColumns);
            this.source2_selectedColumns = [...event.columns];
        }

        this.saveColumnPreferences(source as 'source1' | 'source2', reorderedColumns);

        // Trigger column matching dialog after column preferences are applied
        this.triggerColumnMatchingAfterApply(source);
    };

    private triggerColumnMatchingAfterApply(source: 'source1' | 'source2'): void {
        // Prevent triggering if we're currently applying matched columns (to avoid infinite loop)
        if (this.isApplyingMatchedColumns) {
            return;
        }

        // Get the selected columns from the table (not the aggregation dropdown)
        const selectedColumns = source === 'source1' ? this.source1_selectedColumns : this.source2_selectedColumns;

        if (!selectedColumns || selectedColumns.length === 0) {
            return;
        }

        // Open column matching dialog once with all selected columns
        this.openColumnMatchingDialog(source);
    }

    /**
     * Reset column preferences to default
     */
    public resetColumnPreferences = (source: 'source1' | 'source2') => {
        const appRestApiName = this.reconRestApiName || 'default';
        const entity =
            source === 'source1'
                ? this.isDetached
                    ? this.detachedSource1Entity?.uid
                    : this.isReconEntity
                    ? this.nonDetachedEntity?.uid
                    : this.selectedEntity?.uid
                : this.isDetached
                ? this.detachedSource2Entity?.uid
                : this.isReconEntity
                ? this.nonDetachedEntity?.uid
                : this.selectedEntity?.uid;

        if (!entity) {
            return;
        }

        const key = `apps.${appRestApiName}.reconciliation.${entity}.${source}.table.fields`;

        // Remove the preference from userPreferences
        this.userPreferences.removePreference(key);

        // Rebuild default columns
        this.prepareTableColumns();
    };

    private getSelectedEntityRecords = (source?: 'source1' | 'source2') => {
        this.store$.dispatch(
            GetFilingInstanceEntityRecords({
                reconSpecific: this.isReconEntity,
                source: this.isReconEntity ? source || (!this.isDetached ? 'grouped' : undefined) : undefined,
            })
        );
    };

    public clickedRecord = (record: TableData) => {
        this.store$.dispatch(
            SetFilingInstanceEntityView({
                recordId: record.recordId,
                viewLevel: this.currentView?.level,
                entityId: this.currentView?.entityId,
            })
        );
    };

    public changedPage = (event: PageEvent, sourceType?: 'source1' | 'source2') => {
        const isPageSizeChanged =
            (sourceType === 'source2' ? this.source2Pagination.size : this.source1Pagination.size) !== event?.pageSize;

        if (isPageSizeChanged) {
            this.resetSelection(sourceType);
        } else {
            this.prepareIndexVsSelectedRecords();
        }
        this.prepareTotalCount();
        switch (sourceType) {
            case 'source1':
                this.store$.dispatch(
                    SetFilingInstanceEntityPagination({
                        pageIndex: this.source1Pagination.size !== event?.pageSize ? 0 : event?.pageIndex,
                        pageSize: event?.pageSize,
                        reconSpecific: sourceType,
                        entityId: this.isDetached ? this.detachedSource1EntityId : undefined,
                    })
                );
                break;
            case 'source2':
                this.store$.dispatch(
                    SetFilingInstanceEntityPagination({
                        pageIndex: this.source2Pagination.size !== event?.pageSize ? 0 : event?.pageIndex,
                        pageSize: event?.pageSize,
                        reconSpecific: sourceType,
                        entityId: this.isDetached ? this.detachedSource2EntityId : undefined,
                    })
                );
                break;
            default:
                this.store$.dispatch(
                    SetFilingInstanceEntityPagination({
                        pageIndex: this.source1Pagination.size !== event?.pageSize ? 0 : event?.pageIndex,
                        pageSize: event?.pageSize,
                        reconSpecific: this.isReconEntity && !this.isDetached ? 'grouped' : undefined,
                        entityId: this.isReconEntity ? this.nonDetachedEntityId : undefined,
                    })
                );
                break;
        }
    };

    showSelectedRecords = () => {
        this.openShowSelectedRecordsDialog();
    };

    private openShowSelectedRecordsDialog = () => {
        const dialogRef = this.dialog.open(InstanceReconcileDataDialogComponent, {
            panelClass: ['instanceDialog'],
            disableClose: false,
            width: '98vw',
            height: '97vh',
            maxWidth: '100vw',
        });
        dialogRef.componentInstance.demoMode = true;
        dialogRef.componentInstance.dialogRef = dialogRef;
        dialogRef.componentInstance.associationId = this.associationId;
        dialogRef.componentInstance.source1 = this.source1;
        dialogRef.componentInstance.source1UniqueId = this.source1UniqueId;
        dialogRef.componentInstance.source2 = this.source2;
        dialogRef.componentInstance.source2UniqueId = this.source2UniqueId;
        dialogRef.componentInstance.nonDetachedEntity = this.nonDetachedEntity;
        dialogRef.componentInstance.detachedSource1Entity = this.detachedSource1Entity;
        dialogRef.componentInstance.detachedSource2Entity = this.detachedSource2Entity;
        dialogRef.componentInstance.entityRecords = this.getSelectedRecords();
        dialogRef.componentInstance.isDetached = this.isDetached;
        dialogRef.componentInstance.reconConfiguration = this.reconConfiguration;
        dialogRef.componentInstance.nonDetachedEntityId = this.nonDetachedEntityId;
        dialogRef.componentInstance.nonReconEntities = this.nonReconEntities;
        dialogRef.componentInstance.detachedSource1UniqueFieldId = this.detachedSource1UniqueFieldId;
        dialogRef.componentInstance.detachedSource2UniqueFieldId = this.detachedSource2UniqueFieldId;
    };

    private getSelectedRecords = () => {
        if (this.demoMode) {
            return this.entityRecords;
        }
        const records: {
            source1: Record[];
            source2: Record[];
        } = {
            source1: [],
            source2: [],
        };
        Object.keys(this.pageIndexVsRecords).forEach(source => {
            Object.keys(this.pageIndexVsRecords[source]).forEach(row => {
                this.pageIndexVsRecords[source][row as any as number].records.forEach(record => {
                    if (!records[source].some(r => r.id === record.id)) {
                        records[source].push(record);
                    }
                });
            });
        });
        return records;
    };

    private prepareIndexVsSelectedRecords = () => {
        const records = [...[...this.source1tableData.value, ...this.source2tableData.value]];
        const unique1 = !this.isDetached && this.source1UniqueId;
        const unique2 = !this.isDetached && this.source2UniqueId;
        const unique1Ids = [];
        const unique2Ids = [];
        const ids = records
            .filter(row => row.selected)
            .reduce((ids, row) => {
                !ids.includes(row.recordId) && ids.push(row.recordId);
                const unique1Exists = !unique1Ids.includes(row[unique1]);
                unique1 && row[unique1] && !row.hideSource1Checkbox && !unique1Ids.includes(row[unique1]) && unique1Ids.push(row[unique1]);
                !unique1Exists &&
                    unique2 &&
                    row[unique2] &&
                    !row.hideSource2Checkbox &&
                    !unique2Ids.includes(row[unique2]) &&
                    unique2Ids.push(row[unique2]);
                return ids;
            }, []);
        if (!this.pageIndexVsRecords.source1[this.source1Pagination.index]) {
            this.pageIndexVsRecords.source1[this.source1Pagination.index] = {
                records: [],
                tableData: [],
            };
        }
        if (!this.pageIndexVsRecords.source2[this.source2Pagination.index]) {
            this.pageIndexVsRecords.source2[this.source2Pagination.index] = {
                records: [],
                tableData: [],
            };
        }
        this.pageIndexVsRecords.source1[this.source1Pagination.index] = {
            records: this.entityRecords.source1
                .filter(record => ids.includes(record.id) || unique1Ids.includes(record.fields.find(row => row.id === unique1)?.value))
                .filter(
                    record =>
                        !!this.source1tableData.value.find(row => record.id === row.recordId) ||
                        !!unique1Ids.includes(record.fields.find(row => row.id === unique1)?.value)
                ),
            tableData: this.source1tableData.value.filter(row => row.selected && row.recordId),
        };
        this.pageIndexVsRecords.source2[this.source2Pagination.index] = {
            records: this.entityRecords?.[this.isDetached ? 'source2' : 'source1']
                .filter(record => ids.includes(record.id) || unique2Ids.includes(record.fields.find(row => row.id === unique2)?.value))
                .filter(
                    record =>
                        !!this.source2tableData.value.find(row => record.id === row.recordId) ||
                        unique2Ids.includes(record.fields.find(row => row.id === unique2)?.value)
                ),
            tableData: this.source2tableData.value.filter(row => row.selected && row.recordId),
        };
    };

    private makeAllRecordsUnSelected = () => {
        const source1Data = this.source1tableData.value.map(obj => ({ ...obj, selected: false }));
        this.source1tableData.next(source1Data);
        const source2Data = this.source2tableData.value.map(obj => ({ ...obj, selected: false }));
        this.source2tableData.next(source2Data);
    };

    private resetSelection = (sourceType?: 'source1' | 'source2') => {
        if (!sourceType) {
            this.pageIndexVsRecords = {
                source1: {},
                source2: {},
            };
            this.selectedRecordsCount = 0;
        } else {
            this.pageIndexVsRecords[sourceType] = {};
        }
        this.makeAllRecordsUnSelected();
    };

    public initiateWorkFlowAction = () => {
        if (this.selectedWorkflowAction === undefined || this.selectedWorkflowAction?.name?.length === 0) {
            this._utils.alertError('Please select a workflow action.');
            return;
        }
        if (this.validateTolerances(this.continueWorkflowAction)) {
            this.continueWorkflowAction();
        }
    };

    private continueWorkflowAction = () => {
        const recordsSplit = this.getSelectedRecords();
        const nonDetachedEntity = {
            entityId: this.nonDetachedEntity.uid,
            recordIds: [],
        };
        const source1RecordIds = {
            entityId: this.nonDetachedEntity.uid,
            recordIds: [],
        };
        const source2RecordIds = {
            entityId: this.nonDetachedEntity.uid,
            recordIds: [],
        };
        if (this.isDetached) {
            this.detachedSource1EntityId && (source1RecordIds.entityId = this.detachedSource1EntityId);
            recordsSplit.source1
                .filter(record => !source1RecordIds.recordIds.find(item => item === record.id))
                .forEach(record => source1RecordIds.recordIds.push(record.id));

            this.detachedSource2EntityId && (source2RecordIds.entityId = this.detachedSource2EntityId);
            recordsSplit.source2
                .filter(record => !source2RecordIds.recordIds.find(item => item === record.id))
                .forEach(record => source2RecordIds.recordIds.push(record.id));
        } else {
            Object.values(recordsSplit)?.forEach(records => {
                records
                    .filter(record => !nonDetachedEntity.recordIds.find(item => item === record.id))
                    .forEach(record => nonDetachedEntity.recordIds.push(record.id));
            });
            if (this.selectedWorkflowAction === undefined || this.selectedWorkflowAction?.name?.length === 0) {
                this._utils.alertError('Please select a workflow action.');
                return;
            } else if (nonDetachedEntity.recordIds.length === 0) {
                this._utils.alertError('Please select at least one record.');
                return;
            }
        }
        this.dispatchWorkflowAction(source1RecordIds, source2RecordIds, nonDetachedEntity);
    };

    private dispatchWorkflowAction = (
        source1RecordIds: { entityId: string; recordIds: string[] },
        source2RecordIds: { entityId: string; recordIds: string[] },
        nonDetachedEntity: { entityId: string; recordIds: string[] }
    ) => {
        this.instanceCutoffTimestamp = CommonUtilsService.getOrgAndUserOffsetTimeStamp();
        this.store$.dispatch(
            ExecuteWorkflowStage({
                apiAction: this.selectedWorkflowAction.name,
                comment: '',
                formFields: {},
                instanceIds: [this.instanceId],
                serviceId: this.serviceId,
                executeTimestamp: this.instanceCutoffTimestamp,
                actionVariables: {
                    selectedEntityRecords: {
                        records: [...(this.isDetached ? [source1RecordIds, source2RecordIds] : [nonDetachedEntity])],
                    },
                },
            })
        );
        this.selectedWorkflowAction = undefined;
    };

    public onColumnSearch = (data: any, source?: 'source1' | 'source2') => {
        const clonedData = CommonUtilsService.cloneObject(data);
        const entity = this.isReconEntity
            ? this.isDetached
                ? source === 'source1'
                    ? this.detachedSource1Entity
                    : this.detachedSource2Entity
                : this.nonDetachedEntity
            : this.selectedEntity;
        const field = entity.fields.find(field => field.uid === clonedData?.name);
        if (field.datatype === 'DATE' && clonedData?.value) {
            const dateCheck = CommonUtilsService.transformDate(clonedData.value as string, field.outputFormat as any, 'dd-mm-yyyy');
            if (!dateCheck || dateCheck.includes('undefined') || dateCheck.includes('null')) {
                this._utils.alertError('Enter valid date');
                return;
            } else {
                clonedData.value = dateCheck;
            }
        }
        const columnExists = this.searchedColumnsData.findIndex(c => c.name === clonedData.name);
        columnExists === -1 && this.searchedColumnsData.push(clonedData);
        for (let j = 0; j < this.searchedColumnsData.length; j++) {
            if (
                this.source1_selectedColumns.indexOf(this.searchedColumnsData?.[j]?.name) === -1 &&
                this.source2_selectedColumns.indexOf(this.searchedColumnsData?.[j]?.name) === -1
            ) {
                this.searchedColumnsData?.splice(j, 1);
            }
        }
        const obj = {};
        for (let i = 0; i < this.searchedColumnsData.length; i++) {
            if (this.searchedColumnsData[i].name === clonedData.name) {
                this.searchedColumnsData[i].value = clonedData.value;
                this.searchedColumnsData[i].value && (obj[this.searchedColumnsData[i].name] = this.searchedColumnsData[i].value);
            } else {
                this.searchedColumnsData[i].value && (obj[this.searchedColumnsData[i].name] = this.searchedColumnsData[i].value);
            }
        }
        this.store$.dispatch(
            ClearRecordsPagination({
                organizationId: this.organizationId,
                serviceId: this.serviceId,
                instanceId: this.instanceId,
                entityUid: this.isReconEntity ? this.nonDetachedEntity.uid : this.selectedEntity?.uid,
                parentRecordId: this.isReconEntity ? undefined : this.selectedDetails?.recordId,
                reconSpecific: this.isReconEntity ? source : undefined,
            })
        );
        if (this._utils.isEmpty(obj)) {
            this.reconCriteriaSource1 = this.prepareReconCriteria('source1');
            this.reconCriteriaSource2 = this.prepareReconCriteria(this.isDetached ? 'source2' : 'source1');
            this.createCriteria(source);
            this.getSelectedEntityRecords(source);
        } else {
            const criteria = this.prepareCriteria(
                obj,
                source ? { name: source === 'source1' ? this.source1 : this.source2, source } : undefined
            );
            this.reconCriteriaSource1 = this.prepareCriteria(obj, { name: this.source1, source: 'source1' });
            this.reconCriteriaSource2 = this.prepareCriteria(obj, {
                name: this.isDetached ? this.source2 : this.source1,
                source: this.isDetached ? 'source2' : 'source1',
            });
            this.store$.dispatch(
                SetFilingInstanceEntityCriteria({
                    source: this.isReconEntity ? (!this.isDetached ? 'grouped' : source) : undefined,
                    criteria,
                    entityId: this.isReconEntity ? entity?.uid : undefined,
                })
            );
            this.store$.dispatch(
                GetFilingInstanceEntityRecords({
                    reconSpecific: this.isReconEntity,
                    source: this.isReconEntity ? source || (!this.isDetached ? 'grouped' : undefined) : undefined,
                })
            );
        }
    };

    private prepareReconCriteria = (source?: any) => {
        const obj = {};
        for (let i = 0; i < this.searchedColumnsData.length; i++) {
            this.searchedColumnsData[i].value && (obj[this.searchedColumnsData[i].name] = this.searchedColumnsData[i].value);
        }
        return this.prepareCriteria(obj, source ? { name: source === 'source1' ? this.source1 : this.source2, source } : undefined);
    };

    public prepareCriteria = (obj: any, sourceTable?: { source: string; name: string }, fetchingAggregation?: boolean) => {
        let size: number;
        switch (sourceTable?.source) {
            case 'source2':
                size = this.source2Pagination.size;
                break;
            default:
                size = this.source1Pagination.size;
                break;
        }
        const entityId = this.isReconEntity
            ? this.isDetached
                ? sourceTable?.source === 'source1'
                    ? this.detachedSource1EntityId
                    : this.detachedSource2EntityId
                : this.nonDetachedEntityId
            : this.selectedEntity?.uid;
        const filterCriteria = {
            serviceId: this.serviceId,
            entityFilterCriterias: [],
            criteriaRelations: {
                condition: 'AND',
                rules: [],
                restrictionType: 'JoinedRestrictionMetadata',
            },
        };
        let currentEntityCriteria = {
            entityUid: entityId,
            criteriaDefinitions: [
                {
                    type: 'CriteriaDefinition',
                    criteriaName: entityId,
                    restriction: {
                        condition: 'AND',
                        rules: this.prepareRules(obj, sourceTable, fetchingAggregation),
                        restrictionType: 'JoinedRestrictionMetadata',
                    },
                },
            ],
        };
        if (currentEntityCriteria?.criteriaDefinitions?.[0]?.restriction?.rules?.length === 0) {
            currentEntityCriteria = undefined;
        } else {
            filterCriteria?.entityFilterCriterias.push(currentEntityCriteria);
            filterCriteria?.criteriaRelations.rules.push({
                operator: 'EQ',
                value: currentEntityCriteria?.criteriaDefinitions?.[0]?.criteriaName,
                fieldId: 'Criteria',
                restrictionType: 'SingleRestrictionMetadata',
            });
        }
        return {
            entityId,
            restApiServiceName: this.assetMetaData.restAPIName,
            filterCriteria,
            size,
            sort: this.prepareSortCriteria(),
        };
    };

    private prepareRules = (obj: any, sourceTable?: { source: string; name: string }, fetchingAggregation?: boolean) => {
        const rules = [
            {
                fieldId: 'assetDataId',
                operator: 'EQ',
                value: this.instanceId,
                restrictionType: 'SingleRestrictionMetadata',
            },
        ];
        if (this.isReconEntity && this.isDetached) {
            if (sourceTable?.source === 'source1') {
                this.detachedSource1UniqueFieldId &&
                    rules.push({
                        operator: 'EXISTS',
                        value: undefined,
                        fieldId: this.detachedSource1UniqueFieldId,
                        restrictionType: 'SingleRestrictionMetadata',
                    });
                this.nonDetachedGroupNameFieldId &&
                    this.detachedSource1EntityId === this.nonDetachedEntityId &&
                    rules.push({
                        operator: 'EQ',
                        value: this.selectedGroup.id,
                        fieldId: this.nonDetachedGroupNameFieldId,
                        restrictionType: 'SingleRestrictionMetadata',
                    });
                this.source1DetachedGroupNameFieldId &&
                    this.detachedSource1EntityId !== this.nonDetachedEntityId &&
                    rules.push({
                        operator: 'EQ',
                        value: this.selectedGroup.id,
                        fieldId: this.source1DetachedGroupNameFieldId,
                        restrictionType: 'SingleRestrictionMetadata',
                    });
            } else if (sourceTable?.source === 'source2') {
                this.detachedSource2UniqueFieldId &&
                    rules.push({
                        operator: 'EXISTS',
                        value: undefined,
                        fieldId: this.detachedSource2UniqueFieldId,
                        restrictionType: 'SingleRestrictionMetadata',
                    });
                this.nonDetachedGroupNameFieldId &&
                    this.detachedSource2EntityId === this.nonDetachedEntityId &&
                    rules.push({
                        operator: 'EQ',
                        value: this.selectedGroup.id,
                        fieldId: this.nonDetachedGroupNameFieldId,
                        restrictionType: 'SingleRestrictionMetadata',
                    });
                this.source2DetachedGroupNameFieldId &&
                    this.detachedSource2EntityId !== this.nonDetachedEntityId &&
                    rules.push({
                        operator: 'EQ',
                        value: this.selectedGroup.id,
                        fieldId: this.source2DetachedGroupNameFieldId,
                        restrictionType: 'SingleRestrictionMetadata',
                    });
            }
        } else if (this.isReconEntity) {
            !fetchingAggregation &&
                this.associationId &&
                rules.push({
                    operator: 'EXISTS',
                    value: undefined,
                    fieldId: this.associationId,
                    restrictionType: 'SingleRestrictionMetadata',
                });
            sourceTable &&
                rules.push({
                    operator: 'EXISTS',
                    value: undefined,
                    fieldId: sourceTable.source === 'source1' ? this.source1UniqueId : this.source2UniqueId,
                    restrictionType: 'SingleRestrictionMetadata',
                });
            this.nonDetachedGroupNameFieldId &&
                rules.push({
                    operator: 'EQ',
                    value: this.selectedGroup.id,
                    fieldId: this.nonDetachedGroupNameFieldId,
                    restrictionType: 'SingleRestrictionMetadata',
                });
        }
        const entity = this.isReconEntity
            ? this.isDetached
                ? sourceTable?.source === 'source1'
                    ? this.detachedSource1Entity
                    : this.detachedSource2Entity
                : this.nonDetachedEntity
            : this.selectedEntity;
        Object.entries(obj).forEach(([key, value]) => {
            let type, input: any;
            const field = entity.fields.find(field => field.uid === key);
            const isMultipleValues = Array.isArray(value) || (typeof value === 'string' && value.includes(','));
            if (!field || (this.isDetached && field.fieldSection !== undefined && field.fieldSection !== sourceTable.name)) {
                return;
            }
            switch (field.datatype) {
                case 'DOUBLE':
                case 'DECIMAL':
                case 'FLOAT':
                case 'INTEGER':
                    type = 'IN';
                    input = [value];
                    break;
                case 'DATE':
                    type = 'EQ';
                    input = value;
                    break;
                default:
                    type = isMultipleValues ? 'IN' : 'CONTAINS';
                    input = isMultipleValues ? (typeof value === 'string' ? value.split(',').map(v => v.trim()) : value) : `*${value}*`;
            }
            rules.push({
                operator: type,
                value: input,
                fieldId: key,
                restrictionType: 'SingleRestrictionMetadata',
            });
        });
        return rules;
    };

    private prepareSortCriteria = () => {
        const obj = {};
        if (this.isReconEntity && this.isDetached) {
            // Do Nothing
        } else if (this.isReconEntity) {
            obj[this.associationId] = 'ASC';
        }
        return obj;
    };

    private updateAlreadySelectedRecords = (records: Record[]) => {
        const selectedRecords = this.getSelectedTableData();
        const ids = selectedRecords?.map(record => record.recordId);
        this.selectedRecordsCount = selectedRecords.length;
        if (selectedRecords.length > 0) {
            records.forEach(record => {
                if (ids.includes(record.id)) {
                    record.selected = true;
                }
            });
        }
    };

    private getSelectedTableData = () => {
        let tableData: TableData[] = [];
        Object.values(this.pageIndexVsRecords.source1).forEach(value => {
            tableData = [...tableData, ...value.tableData];
        });
        Object.values(this.pageIndexVsRecords.source2).forEach(value => {
            tableData = [...tableData, ...value.tableData];
        });
        return tableData?.filter(row => row.recordId !== undefined);
    };

    private createCriteria = (source?: 'source1' | 'source2') => {
        if (!this.isReconEntity) {
            this.store$.dispatch(
                SetFilingInstanceEntityCriteria({
                    criteria: undefined,
                    source: undefined,
                    entityId: undefined,
                })
            );
            return;
        }
        if (!this.isDetached) {
            const criteria = this.prepareCriteria({});
            this.store$.dispatch(
                SetFilingInstanceEntityCriteria({
                    criteria: criteria,
                    source: 'grouped',
                    entityId: this.nonDetachedEntity?.uid,
                })
            );
            return;
        }
        [
            { source: 'source1', name: this.source1 },
            { source: 'source2', name: this.source2 },
        ].forEach(sourceObj => {
            if (!source || source === sourceObj.source) {
                const criteria = this.prepareCriteria({}, sourceObj);
                this.store$.dispatch(
                    SetFilingInstanceEntityCriteria({
                        criteria: criteria,
                        source: sourceObj.source as any,
                        entityId: sourceObj.source === 'source1' ? this.detachedSource1EntityId : this.detachedSource2EntityId,
                    })
                );
            }
        });
    };

    public onNonReconEntitySelect = () => {
        this.isReconEntity = false;
        this.reconCriteriaSource1 = undefined;
        this.reconCriteriaSource2 = undefined;
        this.source1tableData.next([]);
    };

    private onNonReconEntitySelectionChange = () => {
        this.clearNonReconEntitySearchCriteria();
        this.clearRecordsPagination();
        this.prepareTableColumns();
        this.getSelectedEntityRecords();
    };

    public toggleGroup = (group: any) => {
        group.expanded = !group.expanded;
    };

    public selectGroup = (group: ReconciliationConfiguration['groups'][0]) => {
        if (group.id !== this.previousSelectedEntityUid) {
            this.selectedTable1Id = '';
            this.selectedTable2Id = '';
            this.previousSelectedEntityUid = this.selectedEntity?.uid;
        }
        if (!this.isReconEntity) {
            this.store$.dispatch(
                SetFilingInstanceEntityView({
                    entityId: this.nonDetachedEntityId,
                    viewLevel: 0,
                    recordId: undefined,
                })
            );
        }
        this.clearAggregationData.next(true);
        this.isReconEntity = true;
        this.isDetached = group.detached || group.id === this.reconConfiguration?.mismatchGroup;
        this.prepareTableColumns();
        this.searchedColumnsData = [];
        this.clearSearchCriteria();
        this.clearRecordsPagination();
        this.resetSelection();
        this.selectedWorkflowAction = undefined;
        this.selectedGroup = group;
        this.createCriteria();
        this.onGroupSelectionChange();
        this.getAggregationCardinalityCount();
        this.subscribeToAggregations();
        this.reconCriteriaSource1 = this.prepareReconCriteria('source1');
        this.reconCriteriaSource2 = this.prepareReconCriteria(this.isDetached ? 'source2' : 'source1');
    };

    private clearNonReconEntitySearchCriteria = () => {
        this.selectedEntity?.uid &&
            this.store$.dispatch(
                ClearSearchCriteria({
                    organizationId: this.organizationId,
                    serviceId: this.serviceId,
                    instanceId: this.instanceId,
                    entityUid: this.selectedEntity.uid,
                    parentRecordId: this.selectedDetails?.recordId,
                })
            );
    };

    private clearSearchCriteria = () => {
        if (this.isDetached) {
            this.store$.dispatch(
                ClearSearchCriteria({
                    organizationId: this.organizationId,
                    serviceId: this.serviceId,
                    instanceId: this.instanceId,
                    entityUid: this.detachedSource1EntityId,
                    parentRecordId: undefined,
                })
            );
            this.store$.dispatch(
                ClearSearchCriteria({
                    organizationId: this.organizationId,
                    serviceId: this.serviceId,
                    instanceId: this.instanceId,
                    entityUid: this.detachedSource2EntityId,
                    parentRecordId: undefined,
                })
            );
        } else {
            this.store$.dispatch(
                ClearSearchCriteria({
                    organizationId: this.organizationId,
                    serviceId: this.serviceId,
                    instanceId: this.instanceId,
                    entityUid: this.nonDetachedEntityId,
                    parentRecordId: undefined,
                })
            );
        }
    };

    private clearRecordsPagination = () => {
        if (!this.isReconEntity) {
            this.store$.dispatch(
                ClearRecordsPagination({
                    organizationId: this.organizationId,
                    serviceId: this.serviceId,
                    instanceId: this.instanceId,
                    entityUid: this.selectedEntity?.uid,
                    parentRecordId: this.selectedDetails?.recordId,
                })
            );
        } else if (this.isDetached) {
            this.store$.dispatch(
                ClearRecordsPagination({
                    organizationId: this.organizationId,
                    serviceId: this.serviceId,
                    instanceId: this.instanceId,
                    entityUid: this.detachedSource1EntityId,
                    parentRecordId: undefined,
                })
            );
            this.store$.dispatch(
                ClearRecordsPagination({
                    organizationId: this.organizationId,
                    serviceId: this.serviceId,
                    instanceId: this.instanceId,
                    entityUid: this.detachedSource2EntityId,
                    parentRecordId: undefined,
                })
            );
        } else {
            this.store$.dispatch(
                ClearRecordsPagination({
                    organizationId: this.organizationId,
                    serviceId: this.serviceId,
                    instanceId: this.instanceId,
                    entityUid: this.nonDetachedEntityId,
                    parentRecordId: undefined,
                })
            );
        }
    };

    private storeTags = () => {
        let tags = [];
        if (this.selectedAppConfigurator?.wrapperUI?.clearanceView?.supportsClearanceView) {
            tags = Object.entries(this.selectedAppConfigurator?.wrapperUI?.clearanceView || {}).map(([key, value]) => ({
                tagKey: key,
                tagValue: value,
            }));
        } else if (this.app?.tags) {
            tags = this.app?.tags || [];
        }
        tags.map(entry => {
            switch (entry?.tagKey) {
                case 'output-source-1':
                case 'source1OutputSectionId':
                    this.source1 = entry.tagValue;
                    break;
                case 'output-source-2':
                case 'source2OutputSectionId':
                    this.source2 = entry.tagValue;
                    break;
                case 'group-name-field-id':
                case 'groupNameFieldId':
                    this.nonDetachedGroupNameFieldId = entry.tagValue;
                    break;
                case 'unique-id-source-1':
                case 'source1UniqueFieldId':
                    this.source1UniqueId = entry.tagValue;
                    break;
                case 'unique-id-source-2':
                case 'source2UniqueFieldId':
                    this.source2UniqueId = entry.tagValue;
                    break;
                case 'association-id':
                case 'associationFieldId':
                    this.associationId = entry.tagValue;
                    break;
                case 'non-detached':
                case 'nonDetachedEntityId':
                    this.nonDetachedEntityId = entry.tagValue;
                    break;
                case 'detached-source1':
                case 'source1MisMatchGroupEntityId':
                    this.detachedSource1EntityId = entry.tagValue;
                    break;
                case 'detached-source2':
                case 'source2MisMatchGroupEntityId':
                    this.detachedSource2EntityId = entry.tagValue;
                    break;
            }
        });
        this.source1Details = this.reconConfiguration?.sources?.find(source => source.id === this.source1);
        this.source2Details = this.reconConfiguration?.sources?.find(source => source.id === this.source2);
    };

    private getEntityIdentifier = (entityId: string, fieldId: string) => {
        let pattern = '';
        const reverseEntityId = entityId.split('').reverse();
        let canContinue = true;
        fieldId
            ?.split('')
            .reverse()
            .forEach((character, index) => {
                canContinue = canContinue && character === reverseEntityId[index];
                if (!canContinue) {
                    return;
                }
                pattern = character + pattern;
            });
        pattern =
            pattern === entityId ? (fieldId.includes(`_${pattern}`) ? `_${pattern}` : pattern) : pattern.substring(pattern.indexOf('_'));
        if (pattern[0] !== '_' && pattern !== entityId) {
            pattern = '';
        }
        return pattern;
    };

    private prepareVariables = () => {
        if (!this.detachedSource1EntityId && !this.detachedSource2EntityId) {
            return;
        }
        const pattern = this.getEntityIdentifier(this.nonDetachedEntityId, this.associationId);
        if (this.detachedSource1EntityId) {
            const source1FieldId = this.assetMetaData.entities.find(entity => entity.uid === this.detachedSource1EntityId)?.fields?.[0]
                ?.uid;
            const source1Pattern = this.getEntityIdentifier(this.detachedSource1EntityId, source1FieldId);
            const source1UniqueIdWithoutPattern =
                pattern?.length > 0 ? this.source1UniqueId.substring(0, this.associationId.indexOf(pattern)) : this.source1UniqueId;
            const source1GroupNameWithoutPattern =
                pattern?.length > 0
                    ? this.nonDetachedGroupNameFieldId.substring(0, this.associationId.indexOf(pattern))
                    : this.nonDetachedGroupNameFieldId;
            this.detachedSource1UniqueFieldId =
                source1Pattern?.length > 0
                    ? this.getFieldByPattern(this.detachedSource1Entity, source1Pattern, source1UniqueIdWithoutPattern)
                    : this.source1UniqueId;
            this.source1DetachedGroupNameFieldId =
                source1Pattern?.length > 0
                    ? this.getFieldByPattern(this.detachedSource1Entity, source1Pattern, source1GroupNameWithoutPattern)
                    : undefined;
        }
        if (this.detachedSource2EntityId) {
            const source2FieldId = this.assetMetaData.entities.find(entity => entity.uid === this.detachedSource2EntityId)?.fields?.[0]
                ?.uid;
            const source2Pattern = this.getEntityIdentifier(this.detachedSource2EntityId, source2FieldId);
            const source2UniqueIdWithoutPattern =
                pattern?.length > 0 ? this.source2UniqueId.substring(0, this.associationId.indexOf(pattern)) : this.source2UniqueId;
            const source2GroupNameWithoutPattern =
                pattern?.length > 0
                    ? this.nonDetachedGroupNameFieldId.substring(0, this.associationId.indexOf(pattern))
                    : this.nonDetachedGroupNameFieldId;
            this.detachedSource2UniqueFieldId =
                source2Pattern?.length > 0
                    ? this.getFieldByPattern(this.detachedSource2Entity, source2Pattern, source2UniqueIdWithoutPattern)
                    : this.source2UniqueId;
            this.source2DetachedGroupNameFieldId =
                source2Pattern?.length > 0
                    ? this.getFieldByPattern(this.detachedSource2Entity, source2Pattern, source2GroupNameWithoutPattern)
                    : undefined;
        }
    };

    private getFieldByPattern = (entityToCheck: Entity, pattern: string, fieldIdWithoutPattern: string) => {
        const newField = `${fieldIdWithoutPattern}${pattern}`;
        return entityToCheck?.fields?.find(field => field.uid === newField) ? newField : undefined;
    };

    private checkEvents = (logs: NotificationEvent[]) => {
        if (logs?.length === 0) return;
        this.checkInstanceEvents(logs);
    };

    private checkInstanceEvents = (logs: NotificationEvent[]) => {
        const instanceCutoffEvents = logs?.filter(
            event => !this.instanceCutoffTimestamp || new Date(event.createdOn)?.getTime() > this.instanceCutoffTimestamp
        );
        const event = instanceCutoffEvents?.find(log =>
            ['INSTANCE_CREATED', 'TASK_STARTED', 'TASK_FAILED', 'TASK_COMPLETED'].includes(log.eventType)
        );
        switch (event?.eventType) {
            case 'TASK_FAILED':
            case 'TASK_COMPLETED':
                this.resetSelection();
                this.store$.dispatch(GetFilingInstancePrimaryEntityRecords());
                this.selectGroup(this.selectedGroup);
                this.workflowLoadingState = false;
                break;
        }
    };

    private setSelectedRecordsCardinalityCount = () => {
        const source1UniqueFieldId = this.isDetached ? this.detachedSource1UniqueFieldId : this.source1UniqueId;
        const source2UniqueFieldId = this.isDetached ? this.detachedSource2UniqueFieldId : this.source2UniqueId;
        this.aggregationcardinalityCountForSource1 = this.getDistinctFieldValueCount(this.entityRecords?.source1, source1UniqueFieldId);
        this.aggregationcardinalityCountForSource2 = this.getDistinctFieldValueCount(this.entityRecords?.source2, source2UniqueFieldId);
    };

    private getDistinctFieldValueCount = (records: Record[], fieldId: string): number | string => {
        if (!fieldId) {
            return 'N/A';
        }
        const values = new Set();
        records?.forEach(record => {
            const value = record.fields.find(field => field.id === fieldId)?.value;
            value !== undefined && value !== null && value !== '' && values.add(value);
        });
        return values.size;
    };

    private getAggregationCardinalityCount = () => {
        const documentIdentifierFields: {
            uid: string;
            entityUid: string;
            searchableIndex: string;
            dataType: string;
            groupFieldSearchableIndex: string;
            source: 'source1' | 'source2';
        }[] = [];
        this.aggregationcardinalityCountForSource1 = 'N/A';
        this.aggregationcardinalityCountForSource2 = 'N/A';
        if (this.isReconEntity) {
            if (this.isDetached) {
                if (!this.detachedSource1UniqueFieldId) {
                    this.aggregationcardinalityCountForSource1 = 'N/A';
                    this._utils.alertError(translate('Source1 Unique Field ID is not defined'));
                }
                const source1Field = this.detachedSource1Entity.fields.find(field => field.uid === this.detachedSource1UniqueFieldId);
                if (source1Field) {
                    documentIdentifierFields.push({
                        uid: source1Field.uid,
                        entityUid: this.detachedSource1Entity.uid,
                        searchableIndex: source1Field.searchableIndex,
                        dataType: source1Field.datatype,
                        groupFieldSearchableIndex: undefined,
                        source: 'source1',
                    });
                }
                if (!this.detachedSource2UniqueFieldId) {
                    this.aggregationcardinalityCountForSource2 = 'N/A';
                    this._utils.alertError(translate('Source2 Unique Field ID is not defined'));
                }
                const source2Field = this.detachedSource2Entity.fields.find(field => field.uid === this.detachedSource2UniqueFieldId);

                if (source2Field) {
                    documentIdentifierFields.push({
                        uid: source2Field.uid,
                        entityUid: this.detachedSource2Entity.uid,
                        searchableIndex: source2Field.searchableIndex,
                        dataType: source2Field.datatype,
                        groupFieldSearchableIndex: undefined,
                        source: 'source2',
                    });
                }
            } else {
                const groupNameField = this.nonDetachedEntity.fields.find(field => field.uid === this.nonDetachedGroupNameFieldId);

                this.nonDetachedEntity.fields
                    .filter(field => field.uid === this.source1UniqueId || field.uid === this.source2UniqueId)
                    .forEach(field => {
                        documentIdentifierFields.push({
                            uid: field.uid,
                            entityUid: this.nonDetachedEntity.uid,
                            searchableIndex: field.searchableIndex,
                            dataType: field.datatype,
                            groupFieldSearchableIndex: groupNameField?.searchableIndex,
                            source: 'source1',
                        });
                    });
            }
        }
        this.aggregationcardinalityCountForSource2 !== 'N/A' && (this.aggregationcardinalityCountForSource2 = null);
        this.aggregationcardinalityCountForSource1 !== 'N/A' && (this.aggregationcardinalityCountForSource1 = null);
        for (const field of documentIdentifierFields) {
            const Querydata = {
                aggs: {
                    clearance: {
                        cardinality_count: {
                            terms: [
                                {
                                    field: `${field.searchableIndex}${field.dataType === 'STRING' ? '.raw' : ''}`,
                                },
                            ],
                        },
                    },
                },
            };
            const criteria = this.prepareCriteria(
                {},
                { source: field.source, name: field.source === 'source1' ? this.source1 : this.source2 },
                true
            );
            criteria['aggs'] = Querydata.aggs;
            const data = {
                query: criteria,
                searchEntityId: field.entityUid,
                noAlerts: false,
            };
            this.store$.dispatch(
                GetReconCardinalityCount({
                    instanceId: this.instanceId,
                    parentRecordId: this.selectedDetails?.recordId || 'parentRecordId',
                    fieldId: field.searchableIndex,
                    criteria: data,
                })
            );
        }
    };

    private subscribeToAggregations = () => {
        if (!this.selectedGroup?.id) {
            return;
        }
        const nonDetachedFields = this.nonDetachedEntity?.fields || [];
        const documentIdentifierFields: {
            uid: string;
            searchableIndex: string;
            groupFieldSearchableIndex?: string;
        }[] = [];
        if (this.isDetached) {
            const source1Field = this.detachedSource1Entity.fields.find(field => field.uid === this.detachedSource1UniqueFieldId);
            const source2Field = this.detachedSource2Entity.fields.find(field => field.uid === this.detachedSource2UniqueFieldId);

            if (source1Field) {
                documentIdentifierFields.push({
                    uid: source1Field.uid,
                    searchableIndex: source1Field.searchableIndex,
                });
            }
            if (source2Field) {
                documentIdentifierFields.push({
                    uid: source2Field.uid,
                    searchableIndex: source2Field.searchableIndex,
                });
            }
        } else {
            const groupNameField = nonDetachedFields.find(field => field.uid === this.nonDetachedGroupNameFieldId);

            nonDetachedFields
                .filter(field => field.uid === this.source1UniqueId || field.uid === this.source2UniqueId)
                .forEach(field => {
                    documentIdentifierFields.push({
                        uid: field.uid,
                        searchableIndex: field.searchableIndex,
                        groupFieldSearchableIndex: groupNameField?.searchableIndex,
                    });
                });
        }
        documentIdentifierFields.forEach(field => {
            this.store$
                .select(
                    getReconCardinalityCount$(this.instanceId, field.searchableIndex, this.selectedDetails?.recordId || 'parentRecordId')
                )
                .pipe(takeUntil(this.unSubscribe))
                .subscribe(value => {
                    if (field.uid === this.detachedSource1UniqueFieldId || field.uid === this.source1UniqueId) {
                        this.aggregationcardinalityCountForSource1 = value ? value : 'N/A';
                    } else if (field.uid === this.detachedSource2UniqueFieldId || field.uid === this.source2UniqueId) {
                        this.aggregationcardinalityCountForSource2 = value ? value : 'N/A';
                    }
                });
        });
    };

    private createGroupHierarchy = (items: any[]) => {
        const GROUP_SEPARATOR_REGEX = /^(.+?)\s*(?:-|_|\/|:|\|)\s*(.+)$/;
        const groups = {};
        items.forEach(item => {
            const match = item.name.match(GROUP_SEPARATOR_REGEX);
            if (match) {
                const groupName = match[1].trim();
                groups[groupName] = (groups[groupName] || 0) + 1;
            }
        });
        const groupedItems = [];
        const addedGroups = new Set();
        items.forEach(item => {
            const match = item.name.match(GROUP_SEPARATOR_REGEX);
            if (!match) {
                groupedItems.push(item);
                return;
            }
            const groupName = match[1].trim();
            if (groups[groupName] > 1) {
                if (!addedGroups.has(groupName)) {
                    groupedItems.push({
                        name: groupName,
                        grouped: true,
                        childGroup: items
                            .filter(i => {
                                const m = i.name.match(GROUP_SEPARATOR_REGEX);
                                return m && m[1].trim() === groupName;
                            })
                            .map(i => {
                                const [, , right] = i.name.match(GROUP_SEPARATOR_REGEX)!;
                                return {
                                    ...i,
                                    name: right.trim(),
                                };
                            }),
                    });
                    addedGroups.add(groupName);
                }
            } else {
                groupedItems.push(item);
            }
        });

        if (groupedItems.length > 0 && groupedItems[0].grouped) {
            groupedItems[0].expanded = true;
        }

        return groupedItems;
    };

    private initiateSubscriptions = () => {
        this.store$
            .select(getCurrentOrganizationId$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(orgId => {
                this.organizationId = orgId;
            });
        this.store$
            .select(getApp$(this.serviceId))
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(event => {
                this.app = CommonUtilsService.cloneObject(event);
            });
        this.store$
            .select(getSubscribedAppsMap$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(event => {
                this.subscribedApps = event;
                for (const key in this.subscribedApps) {
                    if (this.subscribedApps[key].restApiName === this.reconRestApiName) {
                        this.reconApp = this.subscribedApps[key];
                        break;
                    }
                }
            });
        this.store$
            .select(getReconConfigurations$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(response => {
                const configuration = CommonUtilsService.cloneObject(response?.[this.reconApp?.serviceId]);
                configuration.groups = configuration.groups?.length > 0 ? this.createGroupHierarchy(configuration.groups) : [];
                this.reconConfiguration = configuration;
                if (!this.reconConfiguration) {
                    this.store$.dispatch(
                        GetReconConfiguration({ noAlerts: true, serviceId: this.reconApp?.serviceId, apiName: this.reconApp?.restApiName })
                    );
                }
            });
        this.store$
            .select(getAssetsWithLatestVersion$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(data => {
                this.actualAssetId = data?.[this.app?.serviceId].metadataId;
            });
        this.store$
            .select(getAppConfiguratorsMap$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(configuratorsMap => {
                this.selectedAppConfigurator = CommonUtilsService.cloneObject(
                    configuratorsMap?.[this.serviceId]?.[this.app?.assetMetaUId || this.actualAssetId] || {}
                );
                this.storeTags();
            });
        this.store$
            .select(getFilingPrimaryEntityRecords$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe((records: Record[]) => {
                this.primaryEntityRecord = new Record(records?.[0], this.primaryEntity);
                this.prepareTotalCount();
            });
        this.store$
            .select(getFilingSelectedAppMetaData$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(event => {
                this.assetMetaData = event;
                this.entities = event?.entities;
                this.primaryEntity = this.entities?.find(entity => entity.primary);
                this.nonDetachedEntityId = this.nonDetachedEntityId || this.entities?.find(entity => !entity.primary)?.uid;
                this.nonDetachedEntity = this.entities?.find(entity => entity.uid === this.nonDetachedEntityId);
                if (this.reconConfiguration?.restapiname?.length > 0) {
                    if (!this.detachedSource1EntityId) {
                        this.detachedSource1EntityId = this.nonDetachedEntityId;
                    }
                    if (!this.detachedSource2EntityId) {
                        this.detachedSource2EntityId = this.detachedSource1EntityId;
                    }
                }
                this.detachedSource1Entity =
                    this.detachedSource1EntityId && this.entities?.find(entity => entity.uid === this.detachedSource1EntityId);
                this.detachedSource2Entity =
                    this.detachedSource2EntityId && this.entities?.find(entity => entity.uid === this.detachedSource2EntityId);
                this.prepareVariables();
                this.nonDetachedEntity &&
                    this.store$.dispatch(
                        SetFilingInstanceEntityView({
                            entityId: this.nonDetachedEntity.uid,
                            viewLevel: 0,
                            recordId: undefined,
                        })
                    );
                this.store$.dispatch(
                    SetFilingInstanceEntityIds({
                        detachedSource1EntityId: this.detachedSource1EntityId,
                        detachedSource2EntityId: this.detachedSource2EntityId,
                        nonDetachedSourceEntityId: this.nonDetachedEntityId,
                    })
                );
                if (this.reconConfiguration?.groups?.length > 0) {
                    const firstGroup = this.reconConfiguration.groups[0];
                    if (firstGroup.grouped && firstGroup.childGroup?.length > 0) {
                        this.selectGroup(firstGroup.childGroup[0]);
                    } else {
                        this.selectGroup(firstGroup);
                    }
                }
                this.nonReconEntities = this.entities.filter(
                    entity =>
                        !entity.primary &&
                        entity.uid !== this.nonDetachedEntityId &&
                        (!this.detachedSource1EntityId || entity.uid !== this.detachedSource1EntityId) &&
                        (!this.detachedSource2EntityId || entity.uid !== this.detachedSource2EntityId)
                );
            });
        this.store$
            .select(getFilingNonDetachedGroupRecords$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isDetached && this.isReconEntity)
            )
            .subscribe(records => {
                const clonedRecords = CommonUtilsService.cloneObject(records);
                clonedRecords.length > 0 && this.updateAlreadySelectedRecords(clonedRecords);
                this.loadTableData(clonedRecords);
            });
        this.store$
            .select(getFilingNonDetachedGroupRecordsCount$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isDetached && this.isReconEntity)
            )
            .subscribe(count => {
                this.dataLength = count;
            });
        this.store$
            .select(getFilingNonDetachedGroupRecordsPagination$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isDetached && this.isReconEntity)
            )
            .subscribe(event => {
                this.source1Pagination.index = event?.pageIndex;
                this.source1Pagination.size = event?.pageSize;
                this.source2Pagination.index = event?.pageIndex;
                this.source2Pagination.size = event?.pageSize;
            });
        this.store$
            .select(getFilingDetachedSource1Records$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined && this.isDetached && this.isReconEntity)
            )
            .subscribe(records => {
                const clonedRecords = CommonUtilsService.cloneObject(records);
                clonedRecords.length > 0 && this.updateAlreadySelectedRecords(clonedRecords);
                this.loadTableData(clonedRecords, 'source1');
            });
        this.store$
            .select(getFilingDetachedSource1RecordsCount$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => this.isDetached && this.isReconEntity)
            )
            .subscribe(count => {
                this.source1Pagination.count = count;
            });
        this.store$
            .select(getFilingDetachedSource1RecordsPagination$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => this.isDetached && this.isReconEntity)
            )
            .subscribe(event => {
                this.source1Pagination.index = event?.pageIndex;
                this.source1Pagination.size = event?.pageSize;
            });
        this.store$
            .select(getFilingDetachedSource2Records$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined && this.isDetached && this.isReconEntity)
            )
            .subscribe(records => {
                const clonedRecords = CommonUtilsService.cloneObject(records);
                clonedRecords.length > 0 && this.updateAlreadySelectedRecords(clonedRecords);
                this.loadTableData(clonedRecords, 'source2');
            });
        this.store$
            .select(getFilingDetachedSource2RecordsCount$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => this.isDetached && this.isReconEntity)
            )
            .subscribe(count => {
                this.source2Pagination.count = count;
            });
        this.store$
            .select(getFilingDetachedSource2RecordsPagination$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => this.isDetached && this.isReconEntity)
            )
            .subscribe(event => {
                this.source2Pagination.index = event?.pageIndex;
                this.source2Pagination.size = event?.pageSize;
            });
        this.store$
            .select(getFilingSelectedEntityDetails$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(event => {
                this.currentView = event;
            });
        this.store$
            .select(getClearanceRunNumber$(this.serviceId, this.instanceId))
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(event => {
                this.clearanceRunNumber = event;
            });
        this.loadingStatus = this.store$.select(getReconInstanceRecordsStatus$).pipe(takeUntil(this.unSubscribe));
        this.actions$.pipe(ofType(SetInstanceDataTableRecordSelect), takeUntil(this.unSubscribe), delay(10)).subscribe(() => {
            this.prepareIndexVsSelectedRecords();
            this.prepareTotalCount();
            this.selectedRecordsCount = this.getSelectedTableData().length;
        });
        this.store$
            .select(getAvailableWorkflowActions$(this.serviceId, this.instanceId))
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(data => {
                this.workflowActions = data;
            });
        this.actions$.pipe(ofType(ExecutedWorkflowStage), takeUntil(this.unSubscribe), delay(10)).subscribe(() => {
            this.workflowLoadingState = true;
        });
        this.store$
            .select(getNotifications$(this.instanceId))
            .pipe(
                takeUntil(this.unSubscribe),
                delay(200),
                filter(() => this.workflowLoadingState)
            )
            .subscribe(logs => {
                this.checkEvents(logs);
            });
        this.store$
            .select(getFilingSelectedEntity$)
            .pipe(
                takeUntil(this.unSubscribe),
                delay(200),
                filter(() => !this.isReconEntity)
            )
            .subscribe(entity => {
                this.selectedEntity = entity;
                this.onNonReconEntitySelectionChange();
            });
        this.store$
            .select(getFilingEntityRecords$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isReconEntity)
            )
            .subscribe(records => {
                const clonedRecords = CommonUtilsService.cloneObject(records);
                this.loadTableData(clonedRecords);
            });
        this.store$
            .select(getFilingEntityRecordsCount$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isReconEntity)
            )
            .subscribe(count => {
                this.dataLength = count;
            });
        this.store$
            .select(getFilingRecordsPagination$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isReconEntity)
            )
            .subscribe(event => {
                this.source1Pagination.index = event?.pageIndex;
                this.source1Pagination.size = event?.pageSize;
            });
        this.store$
            .select(getFilingParentEntityDetails$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isReconEntity)
            )
            .subscribe(event => {
                this.selectedDetails = event;
            });
        this.stateName = this.store$
            .select(getFilingState$(this._commonUtils.getFromStorage('gstfiling-redirect-instanceid'), this.serviceId))
            .pipe(takeUntil(this.unSubscribe));
    };

    ngOnInit(): void {
        if (this.demoMode) {
            this.prepareTableColumns();
            switch (this.isDetached) {
                case true:
                    this.loadTableData(this.entityRecords.source1, 'source1');
                    this.loadTableData(this.entityRecords.source2, 'source2');
                    break;
                default:
                    this.loadTableData(this.entityRecords.source1);
                    break;
            }
            this.setSelectedRecordsCardinalityCount();
        } else {
            if (this.instanceId?.length === 0 && this.serviceId?.length === 0) {
                return;
            }
            this.store$.dispatch(
                SetFilingSelectedInstanceDetails({
                    instanceId: this.instanceId,
                    serviceId: this.serviceId,
                })
            );
            this.store$.dispatch(GetAssetWithLatestVersion({ serviceId: this.serviceId }));
            this.initiateSubscriptions();
        }
    }

    ngOnDestroy(): void {
        this.clearRecordsPagination();
        this.resetSelection();
        this.unSubscribe.next(undefined);
        this.unSubscribe.complete();
    }
}
